import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import { Breadcrumb } from "../components/ui/breadcrumb";
import { useNavigate } from "react-router-dom";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import AddItemsDrawer from "../components/add-items-drawer";
import SearchInput from "../components/search-input";
import { RadioGroupCards } from "../components/ui/radio-group-cards";
import {
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Eye,
  Grip,
  GripHorizontal,
  Save,
  Trash2,
} from "lucide-react";
import { useIsMobile } from "../hooks/use-is-mobile";
import MobileFlowLayout, {
  MOBILE_FIELD_CLASS,
  MobileFlowCard,
  MobileFlowField,
} from "../components/mobile/mobile-flow-layout";
import MobileOptionList from "../components/mobile/mobile-option-list";
import {
  cn,
  getBackendErrorMessage,
  getDestinationLabel,
  getQuantityColumnName,
  getSourceLabel,
} from "../lib/utils";
import { CalendarDatePicker } from "../components/ui/calendar-date-picker";
import {
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { format } from "date-fns";
import PreviewMovementModal from "../components/preview-movement-modal";
import { Badge } from "../components/ui/badge";
import { FormStepper } from "../components/ui/form-stepper";
import AutoSuggestion from "../components/auto-suggestion";
import {
  createMovementActivity,
  getWarehouses,
  getSites,
  getVendors,
  getCustomers,
  getSiteInventoryItems,
  CreateMovementActivityPayload,
  SiteInventoryItem,
  Warehouse,
} from "../lib/api";
import { toast } from "sonner";
import { useUser } from "../hooks/use-user";

export interface Item {
  id?: string;
  item_code: string;
  description: string;
  quantity: number;
  unit_weight: number;
  length?: string;
  rental_quantity?: number;
  damaged_quantity?: number;
  typical_hire_week_pc?: number;
  negotiated_hire_week_pc?: number;
  note?: string;
  add_item_type?: string;
  category?: string;
  is_bundle?: boolean;
  bundle_items?: Item[];
  custom_text?: string;
  inventoryId?: string;
  particulars?: string;
  reason?: string;
}

export const OUTFLOW = "Outflow";
export const INFLOW = "Inflow";
export const DISPOSE = "Dispose";
export const PURCHASE = "Purchase";
export const SELL = "Sell";
export const DELIVER = "Deliver";
export const RETURN = "Return";
export const WAREHOUSE_TRANSFER = "Warehouse Transfer";
export const SITE_TRANSFER = "Site Transfer";
export const LOST = "Lost";

export const STEPS = [
  { id: "activity-type", label: "Choose Activity Type" },
  { id: "activity-details", label: "Fill Activity Details" },
];

const MOBILE_STEPS = [
  { id: STEPS[0].id, label: "Activity type" },
  { id: STEPS[1].id, label: "Details & items" },
];

const INFLOW_ACTIVITIES = [
  {
    label: "Return",
    value: RETURN,
    description: "Site to Warehouse",
    icon: <Icons.Returned className="h-5 w-5" />,
  },
  {
    label: "Transfer",
    value: WAREHOUSE_TRANSFER,
    description: "Warehouse to Warehouse",
    icon: <Icons.WarehouseTransfer className="h-5 w-5" />,
  },
  {
    label: "Purchase",
    value: PURCHASE,
    description: "Vendor to Warehouse",
    icon: <Icons.InventoryPurchase className="h-5 w-5" />,
  },
];

const OUTFLOW_ACTIVITIES = [
  {
    label: "Deliver",
    value: DELIVER,
    description: "Warehouse to Site",
    icon: <Icons.InventoryToSite className="h-5 w-5" />,
  },
  {
    label: "Transfer",
    value: SITE_TRANSFER,
    description: "Site to Site",
    icon: <Icons.SiteTransfer className="h-5 w-5" />,
  },
  {
    label: "Lost",
    value: LOST,
    description: "Items lost at Site",
    icon: <AlertTriangle className="h-5 w-5" />,
  },
  {
    label: "Dispose",
    value: DISPOSE,
    description: "Remove on Warehouse",
    icon: <Icons.InventoryDispose className="h-5 w-5" />,
  },
  {
    label: "Sell",
    value: SELL,
    description: "Warehouse to Customer",
    icon: <Icons.InventoryToSell className="h-5 w-5" />,
  },
];

const RETURN_TYPES = [
  {
    label: "Partial Return",
    value: "Partial",
    description: "Return some of the rented items",
    icon: <GripHorizontal className="h-5 w-5" />,
  },
  {
    label: "Full Return",
    value: "Full",
    description: "Return all rented items on site",
    icon: <Grip className="h-5 w-5" />,
  },
];

const ADD_ACTIVITY_DRAFT_KEY = "addActivityDraft";

interface AddActivityDraft {
  currentStep: string;
  completedSteps: string[];
  activityType: string;
  activitySubType: string;
  source: string;
  destination: string;
  plannedDate: string | null;
  notes: string;
  items: Item[] | null;
  daysHired?: number;
  driverName?: string;
  driverPhoneNumber?: string;
  vehicleNumber?: string;
  returnType?: string;
}

const AddActivity: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(STEPS[0].id);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const currentStepIndex = STEPS.findIndex((step) => step.id === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === STEPS.length - 1;
  const breadcrumbItems = [
    { label: "Movements", href: "/activities" },
    { label: "Create Movement Activity" },
  ];
  const [activityType, setActivityType] = useState("");
  const [activitySubType, setActivitySubType] = useState("");
  const [source, setSource] = useState("");
  const [destination, setDestination] = useState("");
  const [plannedDate, setPlannedDate] = useState<Date | null>(new Date());
  const [notes, setNotes] = useState("");
  const [driverName, setDriverName] = useState("");
  const [driverPhoneNumber, setDriverPhoneNumber] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [daysHired, setDaysHired] = useState<number | "">(0);
  const [items, setItems] = useState<Item[] | null>(null);
  const [itemsSearch, setItemsSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState<Item[] | null>(null);
  const [returnType, setReturnType] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [openOutflowActivities, setOpenOutflowActivities] = useState<
    string | undefined
  >(OUTFLOW);
  const [openInflowActivities, setOpenInflowActivities] = useState<
    string | undefined
  >(INFLOW);
  const useDraftItemsRef = useRef(false);
  const isMobile = useIsMobile();
  const mobileLocationCardRef = useRef<HTMLElement>(null);

  const warehousesQuery = useQuery({
    queryKey: ["warehouses", { page: 1, limit: 1000, search: "" }],
    queryFn: async () => getWarehouses(1, 1000, ""),
  });

  const sitesQuery = useQuery({
    queryKey: ["sites", { page: 1, limit: 1000 }],
    queryFn: async () => getSites(1, 1000),
  });

  const vendorsQuery = useQuery({
    queryKey: ["vendors", { page: 1, limit: 1000, search: "" }],
    queryFn: async () => getVendors(1, 1000),
  });

  const customersQuery = useQuery({
    queryKey: ["customers", { page: 1, limit: 1000 }],
    queryFn: async () => getCustomers(1, 1000),
  });

  useEffect(() => {
    const e =
      warehousesQuery.error ??
      sitesQuery.error ??
      vendorsQuery.error ??
      customersQuery.error;
    if (!e) return;
    toast.error(getBackendErrorMessage(e, "Failed to fetch data"));
  }, [
    warehousesQuery.error,
    sitesQuery.error,
    vendorsQuery.error,
    customersQuery.error,
  ]);

  const warehouses = warehousesQuery.data?.data ?? [];
  const sites = sitesQuery.data?.data ?? [];
  const vendors = vendorsQuery.data?.data ?? [];
  const customers = customersQuery.data?.data ?? [];
  const showInventoryItems = useMemo(() => {
    if (
      activitySubType === WAREHOUSE_TRANSFER ||
      activitySubType === DELIVER ||
      activitySubType === DISPOSE ||
      activitySubType === SELL ||
      activitySubType === PURCHASE ||
      activitySubType === RETURN ||
      activitySubType === SITE_TRANSFER ||
      activitySubType === LOST
    ) {
      return true;
    }

    return false;
  }, [activitySubType]);

  const requiresDestination = useMemo(
    () => activitySubType !== DISPOSE && activitySubType !== LOST,
    [activitySubType],
  );

  const getSiteLabel = (site: any) => {
    const customer = customers.find((c) => c.id === site.customerId);
    const customerName = customer ? customer.fullName : "";
    return `${site.name} - ${customerName} (${site.street}, ${site.ward}, ${site.district}, ${site.region})`;
  };

  const getWarehouseLabel = (warehouse: Warehouse) => {
    return `${warehouse.name} (${warehouse.street}, ${warehouse.ward}, ${warehouse.district}, ${warehouse.region})`;
  };

  const visibleWarehouses = useMemo(() => {
    // Inflow warehouse-to-warehouse transfer: allow picking any warehouse (API still enforces auth).
    if (activityType === INFLOW && activitySubType === WAREHOUSE_TRANSFER) {
      return warehouses;
    }
    if (!user || user.role === "ADMIN") return warehouses;
    const norm = (v: string | null | undefined) =>
      (v ?? "").trim().toLowerCase();
    const uCountry = norm(user.country);
    const uRegion = norm(user.region);
    const uDistrict = norm(user.district);
    const uWard = norm(user.ward);
    const uStreet = norm(user.street);
    if (!uCountry && !uRegion && !uDistrict && !uWard && !uStreet) {
      return warehouses;
    }
    return warehouses.filter((w) => {
      if (uCountry && norm(w.country) !== uCountry) return false;
      if (uRegion && norm(w.region) !== uRegion) return false;
      if (uDistrict && norm(w.district) !== uDistrict) return false;
      if (uWard && norm(w.ward) !== uWard) return false;
      if (uStreet && norm(w.street) !== uStreet) return false;
      return true;
    });
  }, [warehouses, user, activityType, activitySubType]);

  const sourceSuggestions = useMemo(() => {
    switch (activitySubType) {
      case "Return":
      case "Site Transfer":
      case "Lost":
        return sites.map(getSiteLabel);
      case "Warehouse Transfer":
      case "Deliver":
      case "Dispose":
      case "Sell":
        return visibleWarehouses.map(getWarehouseLabel);
      case "Purchase":
        return vendors.map((vendor) => vendor.fullName);
      default:
        return [];
    }
  }, [activitySubType, sites, visibleWarehouses, vendors, customers]);

  const destinationSuggestions = useMemo(() => {
    switch (activitySubType) {
      case "Return":
      case "Warehouse Transfer":
      case "Purchase":
        return visibleWarehouses.map(getWarehouseLabel);
      case "Deliver":
      case "Site Transfer":
        return sites.map(getSiteLabel);
      case "Sell":
        return customers.map((customer) => customer.fullName);
      default:
        return [];
    }
  }, [activitySubType, sites, visibleWarehouses, customers]);

  const selectedSourceSiteId = useMemo(() => {
    const s = sites.find((site) => getSiteLabel(site) === source);
    return s?.id as string | undefined;
  }, [sites, source, customers]);

  const siteInventoryQuery = useQuery({
    queryKey: ["sites", selectedSourceSiteId, "inventory-items"],
    queryFn: async () => {
      if (!selectedSourceSiteId) return [];
      return getSiteInventoryItems(selectedSourceSiteId);
    },
    enabled:
      !!selectedSourceSiteId &&
      !useDraftItemsRef.current &&
      activitySubType === RETURN &&
      returnType === "Full",
  });

  useEffect(() => {
    if (!siteInventoryQuery.error) return;
    toast.error(
      getBackendErrorMessage(siteInventoryQuery.error, "Failed to fetch items"),
    );
  }, [siteInventoryQuery.error]);

  const createActivityMutation = useMutation({
    mutationFn: async (payload: CreateMovementActivityPayload) =>
      createMovementActivity(payload),
    onSuccess: async () => {
      localStorage.removeItem(ADD_ACTIVITY_DRAFT_KEY);
      await queryClient.invalidateQueries({ queryKey: ["activities"] });
      navigate("/activities");
    },
    onError: (error) => {
      toast.error(getBackendErrorMessage(error, "Failed to create movement"));
    },
  });

  const loading = createActivityMutation.isPending;

  const resetFormToDefaults = () => {
    useDraftItemsRef.current = false;
    setCurrentStep(STEPS[0].id);
    setCompletedSteps([]);
    setActivityType("");
    setActivitySubType("");
    setSource("");
    setDestination("");
    setPlannedDate(new Date());
    setNotes("");
    setDriverName("");
    setDriverPhoneNumber("");
    setVehicleNumber("");
    setDaysHired(0);
    setItems(null);
    setItemsSearch("");
    setSelectedItems(null);
    setReturnType("");
    setOpenOutflowActivities(OUTFLOW);
    setOpenInflowActivities(INFLOW);
  };

  const handleClearDraft = () => {
    try {
      localStorage.removeItem(ADD_ACTIVITY_DRAFT_KEY);
      resetFormToDefaults();
      toast.success("Draft cleared");
    } catch {
      toast.error("Failed to clear draft");
    }
  };

  // Clear source/destination if they point at a warehouse outside the user's visible set
  useEffect(() => {
    if (warehouses.length === 0) return;
    const labelIn = (list: Warehouse[], label: string) =>
      list.some((w) => getWarehouseLabel(w) === label);
    if (
      source &&
      labelIn(warehouses, source) &&
      !labelIn(visibleWarehouses, source)
    ) {
      setSource("");
    }
    if (
      destination &&
      labelIn(warehouses, destination) &&
      !labelIn(visibleWarehouses, destination)
    ) {
      setDestination("");
    }
  }, [visibleWarehouses, warehouses, source, destination]);

  // Restore draft from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(ADD_ACTIVITY_DRAFT_KEY);
      if (!raw) return;
      const draft: AddActivityDraft = JSON.parse(raw);
      if ((draft.items?.length ?? 0) > 0) {
        useDraftItemsRef.current = true;
      }
      setCurrentStep(draft.currentStep ?? STEPS[0].id);
      setCompletedSteps(draft.completedSteps ?? []);
      setActivityType(draft.activityType ?? "");
      setActivitySubType(draft.activitySubType ?? "");
      setSource(draft.source ?? "");
      setDestination(draft.destination ?? "");
      setPlannedDate(
        draft.plannedDate ? new Date(draft.plannedDate) : new Date(),
      );
      setNotes(draft.notes ?? "");
      setDriverName(draft.driverName ?? "");
      setDriverPhoneNumber(draft.driverPhoneNumber ?? "");
      setVehicleNumber(draft.vehicleNumber ?? "");
      setDaysHired(draft.daysHired ?? 0);
      setReturnType(draft.returnType ?? "");
      setItems(draft.items ?? null);
      toast.success("Draft restored");
    } catch {
      // ignore invalid or old draft
    }
  }, []);

  const mapSiteInventoryToItems = (
    response: SiteInventoryItem[],
    fillQuantities: boolean,
  ): Item[] =>
    response.filter((item) => (item.totalQuantity ?? 0) > 0).map((item) => ({
      id: item.inventory.id,
      item_code: item.inventory.itemCode,
      description: item.inventory.description,
      length: item.inventory.length,
      quantity: item.totalQuantity,
      unit_weight: Number(item.inventory.unitWeight) || 0,
      rental_quantity: fillQuantities
        ? (item.goodQuantity ?? 0) > 0 || (item.damagedQuantity ?? 0) > 0
          ? (item.goodQuantity ?? 0)
          : item.totalQuantity
        : 0,
      damaged_quantity: fillQuantities ? (item.damagedQuantity ?? 0) : 0,
      inventoryId: item.inventory.id,
      particulars: item.inventory.description,
    }));

  useEffect(() => {
    if (activitySubType !== RETURN) {
      setReturnType("");
    }
  }, [activitySubType]);

  useEffect(() => {
    if (useDraftItemsRef.current) return;
    if (activitySubType !== RETURN) return;
    if (returnType === "Partial") {
      setItems([]);
    }
  }, [activitySubType, returnType, source]);

  useEffect(() => {
    if (useDraftItemsRef.current) return;
    if (activitySubType !== RETURN || returnType !== "Full") return;
    if (!selectedSourceSiteId) {
      setItems([]);
      return;
    }
    setItems(mapSiteInventoryToItems(siteInventoryQuery.data ?? [], true));
  }, [
    activitySubType,
    returnType,
    selectedSourceSiteId,
    siteInventoryQuery.data,
  ]);

  useEffect(() => {
    if (useDraftItemsRef.current) return;
    if (!source) return;

    if (
      activitySubType === RETURN ||
      activitySubType === SITE_TRANSFER ||
      activitySubType === LOST
    ) {
      if (activitySubType !== RETURN) {
        setItems([]);
      }
      return;
    }

    if (visibleWarehouses.some((w) => getWarehouseLabel(w) === source)) {
      setItems([]);
    }
  }, [source, visibleWarehouses, activitySubType]);

  useEffect(() => {
    setItemsSearch("");
  }, [source, activitySubType]);

  const handleDateSelect = (date: Date | null) => {
    setPlannedDate(date);
  };

  const handleNext = () => {
    if (!isLastStep) {
      if (
        showInventoryItems &&
        (activitySubType !== RETURN || returnType === "Partial")
      ) {
        setSelectedItems(items ?? []);
      }
      const nextStep = STEPS[currentStepIndex + 1];
      setCurrentStep(nextStep.id);
      setCompletedSteps([...completedSteps, currentStep]);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      if (activityType === OUTFLOW) {
        setOpenOutflowActivities(OUTFLOW);
      }
      if (activityType === INFLOW) {
        setOpenInflowActivities(INFLOW);
      }
      const previousStep = STEPS[currentStepIndex - 1];
      setCurrentStep(previousStep.id);
    }
  };

  const handleEditActivityTypeLocationAndDate = () => {
    if (activityType === OUTFLOW) {
      setOpenOutflowActivities(OUTFLOW);
    }
    if (activityType === INFLOW) {
      setOpenInflowActivities(INFLOW);
    }
    setCurrentStep(STEPS[0].id);
  };

  const handleSelectActivity = (type: string, subType: string) => {
    if (subType !== activitySubType) {
      useDraftItemsRef.current = false;
      setSource("");
      setDestination(
        subType === DISPOSE || subType === LOST ? "Anywhere" : "",
      );
      setReturnType("");
      setItems(null);
      setItemsSearch("");
      setSelectedItems(null);
    }
    setActivityType(type);
    setActivitySubType(subType);
  };

  const getItemKey = (item: Item) =>
    String(item.id ?? item.inventoryId ?? "");

  const filteredItems = useMemo(() => {
    const list = items ?? [];
    const term = itemsSearch.trim().toLowerCase();
    if (!term) return list;
    return list.filter((item) => {
      const haystack = [
        item.description,
        item.item_code,
        item.length,
        item.particulars,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [items, itemsSearch]);

  const handleRentalQuantityChange = (item: Item, value: string) => {
    const raw = value.replace(/,/g, "");
    if (!/^\d*$/.test(raw)) return;
    let numValue = raw === "" ? 0 : parseInt(raw, 10);
    // Purchase (inflow) allows quantity above available
    if (activitySubType === RETURN) {
      const damaged = item.damaged_quantity ?? 0;
      numValue = Math.min(numValue, Math.max(0, item.quantity - damaged));
    } else if (activitySubType !== PURCHASE && numValue > item.quantity)
      numValue = item.quantity;
    if (numValue < 0) numValue = 0;
    setItems(
      (items ?? []).map((selected) =>
        getItemKey(selected) === getItemKey(item)
          ? {
              ...selected,
              rental_quantity: numValue,
            }
          : selected,
      ),
    );
  };

  const handleDamagedQuantityChange = (item: Item, value: string) => {
    const raw = value.replace(/,/g, "");
    if (!/^\d*$/.test(raw)) return;
    let numValue = Math.max(0, raw === "" ? 0 : parseInt(raw, 10));
    if (activitySubType === RETURN) {
      const good = item.rental_quantity ?? 0;
      numValue = Math.min(numValue, Math.max(0, item.quantity - good));
    }
    setItems(
      (items ?? []).map((selected) =>
        getItemKey(selected) === getItemKey(item)
          ? {
              ...selected,
              damaged_quantity: numValue,
            }
          : selected,
      ),
    );
  };

  const handleReasonChange = (item: Item, reason: string) => {
    setItems(
      (items ?? []).map((selected) =>
        getItemKey(selected) === getItemKey(item)
          ? { ...selected, reason }
          : selected,
      ),
    );
  };

  const handleRemoveItem = (item: Item) => {
    setItems(
      (items ?? []).filter(
        (filterItem) => getItemKey(filterItem) !== getItemKey(item),
      ),
    );
  };

  const sumItems = (getValue: (item: Item) => number) =>
    items && items.length > 0
      ? items.reduce((total: number, item: Item) => total + getValue(item), 0)
      : 0;

  const getReturnTotalQty = (item: Item) =>
    (item.rental_quantity ?? 0) + (item.damaged_quantity ?? 0);

  const isPurchaseWithoutDestinationWarehouse =
    activitySubType === PURCHASE &&
    !visibleWarehouses.some((w) => getWarehouseLabel(w) === destination);

  const isMissingSourceSite =
    (activitySubType === RETURN ||
      activitySubType === SITE_TRANSFER ||
      activitySubType === LOST) &&
    !selectedSourceSiteId;

  const showReturnAddItems =
    activitySubType !== RETURN || returnType === "Partial";

  const handleReturnTypeChange = (value: string) => {
    useDraftItemsRef.current = false;
    setReturnType(value);
  };

  const isCreateDisabled =
    !activityType ||
    !activitySubType ||
    !source ||
    (requiresDestination && !destination) ||
    !plannedDate ||
    !items ||
    items.length === 0 ||
    !items.some(
      (item) => (item.rental_quantity ?? 0) + (item.damaged_quantity ?? 0) > 0,
    ) ||
    loading;

  const handleSaveDraft = () => {
    const draft: AddActivityDraft = {
      currentStep,
      completedSteps,
      activityType,
      activitySubType,
      source,
      destination,
      plannedDate: plannedDate?.toISOString() ?? null,
      notes,
      items,
      ...(activitySubType === RETURN && returnType && { returnType }),
      ...(driverName && { driverName }),
      ...(driverPhoneNumber && { driverPhoneNumber }),
      ...(vehicleNumber && { vehicleNumber }),
      ...((activitySubType === DELIVER ||
        activitySubType === SITE_TRANSFER) && {
        daysHired:
          daysHired === "" || typeof daysHired !== "number" ? 0 : daysHired,
      }),
    };
    try {
      localStorage.setItem(ADD_ACTIVITY_DRAFT_KEY, JSON.stringify(draft));
      toast.success("Draft saved. You can continue later.");
    } catch {
      toast.error("Failed to save draft");
    }
  };

  const handleCreateMovement = async () => {
    if (!items || items.length === 0 || !plannedDate) return;

    try {
      // Map activity types to API format
      const activityTypeMap: { [key: string]: string } = {
        [DELIVER]: "OUTFLOW",
        [RETURN]: "INFLOW",
        [WAREHOUSE_TRANSFER]: "INFLOW",
        [SITE_TRANSFER]: "OUTFLOW",
        [PURCHASE]: "INFLOW",
        [DISPOSE]: "OUTFLOW",
        [SELL]: "OUTFLOW",
        [LOST]: "OUTFLOW",
      };

      const activitySubTypeMap: { [key: string]: string } = {
        [DELIVER]: "DELIVER",
        [RETURN]: "RETURN",
        [WAREHOUSE_TRANSFER]: "TRANSFER",
        [SITE_TRANSFER]: "TRANSFER",
        [PURCHASE]: "PURCHASE",
        [DISPOSE]: "DISPOSE",
        [SELL]: "SELL",
        [LOST]: "LOST",
      };

      // Find source and destination IDs
      const sourceWarehouse = visibleWarehouses.find(
        (w) => getWarehouseLabel(w) === source,
      );
      const sourceSite = sites.find((s) => getSiteLabel(s) === source);
      const sourceVendor = vendors.find((v) => v.fullName === source);

      const destWarehouse = visibleWarehouses.find(
        (w) => getWarehouseLabel(w) === destination,
      );
      const destSite = sites.find((s) => getSiteLabel(s) === destination);
      const destCustomer = customers.find((c) => c.fullName === destination);

      const payload: CreateMovementActivityPayload = {
        sourceSiteId: sourceSite?.id,
        sourceWarehouseId: sourceWarehouse?.id,
        sourceVendorId: sourceVendor?.id,
        destinationSiteId: destSite?.id,
        destinationWarehouseId: destWarehouse?.id,
        destinationCustomerId: destCustomer?.id,
        activityType: activityTypeMap[activitySubType],
        activitySubType: activitySubTypeMap[activitySubType],
        ...((activitySubType === DELIVER ||
          activitySubType === SITE_TRANSFER) && {
          daysHired:
            daysHired === "" || typeof daysHired !== "number" ? 0 : daysHired,
        }),
        items: items
          .filter(
            (item) =>
              (item.rental_quantity ?? 0) + (item.damaged_quantity ?? 0) > 0,
          )
          .map((item) => ({
            particulars: item.description,
            inventoryId: item.inventoryId || "",
            goodQuantity: item.rental_quantity || 0,
            damagedQuantity: item.damaged_quantity || 0,
            ...(item.reason != null &&
              item.reason.trim() !== "" && { reason: item.reason.trim() }),
          })),
        plannedDate: format(plannedDate, "yyyy-MM-dd"),
        notes: notes,
        ...(driverName && { driverName }),
        ...(driverPhoneNumber && { driverPhoneNumber }),
        ...(vehicleNumber && { vehicleNumber }),
      };

      await createActivityMutation.mutateAsync(payload);
    } catch {
      // handled by mutation
    }
  };

  useEffect(() => {
    if (
      currentStep === STEPS[1].id &&
      (!activityType ||
        !activitySubType ||
        !source ||
        (requiresDestination && !destination))
    ) {
      setCurrentStep(STEPS[0].id);
    }
  }, [
    currentStep,
    activityType,
    activitySubType,
    source,
    destination,
    requiresDestination,
  ]);

  useEffect(() => {
    if (activitySubType === DISPOSE) {
      setDestination("Anywhere");
    }
    if (activitySubType === LOST) {
      setDestination("Anywhere");
    }
  }, [activitySubType]);

  const renderLocationAndDate = (fieldClassName?: string) => {
    return (
      <>
        <div className="space-y-2">
          <Label htmlFor="source" className="font-medium">
            From {getSourceLabel(activitySubType)}
          </Label>
          <AutoSuggestion
            value={source}
            suggestions={sourceSuggestions}
            placeholder={`Enter ${getSourceLabel(activitySubType)}`}
            className={fieldClassName}
            onSelect={(value) => {
              useDraftItemsRef.current = false;
              setSource(value);
            }}
          />
        </div>
        {requiresDestination && (
          <div className="space-y-2">
            <Label htmlFor="destination" className="font-medium">
              To {getDestinationLabel(activitySubType)}
            </Label>
            <AutoSuggestion
              value={destination}
              suggestions={destinationSuggestions}
              placeholder={`Enter ${getDestinationLabel(activitySubType)}`}
              className={fieldClassName}
              onSelect={(value) => {
                setDestination(value);
              }}
            />
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="destination" className="font-medium">
            Planned Date
          </Label>
          <CalendarDatePicker
            selected={plannedDate}
            onChange={handleDateSelect}
            className={cn("w-full", fieldClassName)}
          />
        </div>
        {activityType === OUTFLOW &&
          (activitySubType === DELIVER ||
            activitySubType === SITE_TRANSFER) && (
            <div className="space-y-2">
              <Label htmlFor="daysHired" className="font-medium">
                Number of days hired
              </Label>
              <Input
                id="daysHired"
                type="number"
                min={1}
                value={daysHired === "" ? "" : daysHired}
                onChange={(e) => {
                  const raw = e.target.value.replace(/,/g, "");
                  if (raw === "") {
                    setDaysHired("");
                    return;
                  }
                  const num = parseInt(raw, 10);
                  if (!Number.isNaN(num) && num >= 0) setDaysHired(num);
                }}
                placeholder="e.g. 7"
                className={fieldClassName}
              />
            </div>
          )}
      </>
    );
  };

  const flowOverlays = (
    <>
      <PreviewMovementModal
        isPreviewOpen={isPreviewOpen}
        setIsPreviewOpen={setIsPreviewOpen}
        setCurrentStep={setCurrentStep}
        handleEditActivityTypeLocationAndDate={
          handleEditActivityTypeLocationAndDate
        }
        activityType={activityType}
        activitySubType={activitySubType}
        returnType={returnType}
        source={source}
        destination={destination}
        plannedDate={plannedDate ?? new Date()}
        notes={notes}
        items={items ?? []}
      />
      <AddItemsDrawer
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        setItems={setItems}
        activitySubType={activitySubType}
        sourceWarehouseId={
          activitySubType === PURCHASE
            ? (visibleWarehouses.find(
                (w) => getWarehouseLabel(w) === destination,
              )?.id ?? null)
            : (visibleWarehouses.find((w) => getWarehouseLabel(w) === source)
                ?.id ?? null)
        }
        sourceSiteId={
          activitySubType === RETURN ||
          activitySubType === SITE_TRANSFER ||
          activitySubType === LOST
            ? (selectedSourceSiteId ?? null)
            : null
        }
        handleEditActivityTypeLocationAndDate={
          handleEditActivityTypeLocationAndDate
        }
        onClose={() => {
          setSelectedItems(null);
        }}
      />
    </>
  );

  if (isMobile) {
    const selectMobileActivity = (type: string, subType: string) => {
      const isFirstSelection = !activitySubType;
      handleSelectActivity(type, subType);
      if (type === OUTFLOW) setOpenInflowActivities(undefined);
      else setOpenOutflowActivities(undefined);
      if (isFirstSelection) {
        window.setTimeout(() => {
          mobileLocationCardRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 80);
      }
    };
    const showDaysHired =
      activitySubType === DELIVER || activitySubType === SITE_TRANSFER;
    const kg = (weight: number, quantity: number) =>
      `${Math.floor(weight * quantity)} KG`;

    return (
      <>
        {flowOverlays}
        <MobileFlowLayout
          title="Create Movement"
          steps={MOBILE_STEPS}
          currentStepIndex={currentStepIndex}
          onClose={() => navigate("/activities")}
          closeIcon="close"
          actions={[
            {
              label: "Preview",
              icon: Eye,
              onClick: () => setIsPreviewOpen(true),
            },
            {
              label: "Save draft",
              icon: Save,
              onClick: handleSaveDraft,
              disabled: loading,
            },
            {
              label: "Clear draft",
              icon: Trash2,
              onClick: handleClearDraft,
              destructive: true,
              disabled: loading,
            },
          ]}
          onBack={isFirstStep ? undefined : handlePrevious}
          primaryAction={
            isLastStep
              ? {
                  label: "Create Movement",
                  loadingLabel: "Creating...",
                  onClick: handleCreateMovement,
                  disabled: isCreateDisabled,
                  loading,
                }
              : {
                  label: "Next",
                  onClick: handleNext,
                  disabled:
                    !activityType ||
                    !activitySubType ||
                    !source ||
                    !destination,
                }
          }
        >
          {currentStep === STEPS[0].id && (
            <>
              <MobileFlowCard title="Outflow activities">
                <MobileOptionList
                  options={OUTFLOW_ACTIVITIES}
                  value={activitySubType}
                  onChange={(value) => selectMobileActivity(OUTFLOW, value)}
                />
              </MobileFlowCard>
              <MobileFlowCard title="Inflow activities">
                <MobileOptionList
                  options={INFLOW_ACTIVITIES}
                  value={activitySubType}
                  onChange={(value) => selectMobileActivity(INFLOW, value)}
                />
              </MobileFlowCard>
              {activityType && activitySubType && (
                <MobileFlowCard
                  ref={mobileLocationCardRef}
                  title="Location & date"
                  description={`${activityType} · ${activitySubType}`}
                >
                  <div className="space-y-4">
                    {renderLocationAndDate(MOBILE_FIELD_CLASS)}
                  </div>
                </MobileFlowCard>
              )}
            </>
          )}

          {currentStep === STEPS[1].id && (
            <>
              <MobileFlowCard
                title="Movement"
                action={
                  <button
                    type="button"
                    onClick={handleEditActivityTypeLocationAndDate}
                    className="h-9 rounded-xl border-0 bg-primary-50 px-3 text-xs font-semibold text-primary-700 active:bg-primary-100"
                  >
                    Edit
                  </button>
                }
              >
                <dl className="divide-y divide-gray-100">
                  {[
                    {
                      label: "Type",
                      value: `${activityType} - ${activitySubType}`,
                    },
                    { label: "Source", value: source },
                    ...(requiresDestination
                      ? [{ label: "Destination", value: destination }]
                      : []),
                    {
                      label: "Planned date",
                      value: plannedDate
                        ? plannedDate.toLocaleDateString()
                        : "No date selected",
                    },
                    ...(showDaysHired
                      ? [
                          {
                            label: "Days hired",
                            value: `${daysHired === "" || typeof daysHired !== "number" ? "0" : daysHired} days`,
                          },
                        ]
                      : []),
                    ...(activitySubType === RETURN && returnType
                      ? [
                          {
                            label: "Return type",
                            value:
                              returnType === "Full"
                                ? "Full return"
                                : "Partial return",
                          },
                        ]
                      : []),
                  ].map((row) => (
                    <div
                      key={row.label}
                      className="flex items-start justify-between gap-4 py-2.5 first:pt-0 last:pb-0"
                    >
                      <dt className="shrink-0 text-[13px] text-gray-500">
                        {row.label}
                      </dt>
                      <dd className="min-w-0 break-words text-right text-[13px] font-medium text-gray-900">
                        {row.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </MobileFlowCard>

              {activitySubType === RETURN && (
                <MobileFlowCard title="Return type">
                  <MobileOptionList
                    options={RETURN_TYPES}
                    value={returnType}
                    onChange={handleReturnTypeChange}
                  />
                </MobileFlowCard>
              )}

              <MobileFlowCard
                title="Items"
                description={`${items?.length ?? 0} ${(items?.length ?? 0) === 1 ? "item" : "items"}`}
              >
                {activitySubType === RETURN && !returnType ? (
                  <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                    Choose Partial or Full return to continue.
                  </p>
                ) : activitySubType === RETURN &&
                  returnType === "Full" &&
                  siteInventoryQuery.isFetching ? (
                  <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                    Loading items on site…
                  </p>
                ) : !items || items.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                    {activitySubType === RETURN && returnType === "Full"
                      ? "No items on site to return."
                      : "No items yet."}
                  </p>
                ) : (
                  <div className="space-y-1 rounded-xl bg-gray-50 px-3 py-2.5 text-xs text-gray-600">
                    <div className="flex justify-between gap-3">
                      <span>Available</span>
                      <span className="font-semibold tabular-nums text-gray-900">
                        {sumItems((item) => item?.quantity ?? 0)} ·{" "}
                        {sumItems((item) =>
                          Math.floor(item.unit_weight * (item?.quantity ?? 0)),
                        )}{" "}
                        KG
                      </span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{getQuantityColumnName(activitySubType)}</span>
                      <span className="font-semibold tabular-nums text-gray-900">
                        {sumItems((item) => item?.rental_quantity ?? 0)} ·{" "}
                        {sumItems((item) =>
                          Math.floor(
                            item.unit_weight * (item?.rental_quantity ?? 0),
                          ),
                        )}{" "}
                        KG
                      </span>
                    </div>
                    {(activitySubType === PURCHASE ||
                      activitySubType === RETURN) && (
                      <div className="flex justify-between gap-3">
                        <span>Damaged</span>
                        <span className="font-semibold tabular-nums text-gray-900">
                          {sumItems((item) => item?.damaged_quantity ?? 0)} ·{" "}
                          {sumItems((item) =>
                            Math.floor(
                              item.unit_weight * (item?.damaged_quantity ?? 0),
                            ),
                          )}{" "}
                          KG
                        </span>
                      </div>
                    )}
                    {activitySubType === RETURN && (
                      <div className="flex justify-between gap-3">
                        <span>Total</span>
                        <span className="font-semibold tabular-nums text-gray-900">
                          {sumItems(getReturnTotalQty)} ·{" "}
                          {sumItems((item) =>
                            Math.floor(
                              item.unit_weight * getReturnTotalQty(item),
                            ),
                          )}{" "}
                          KG
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </MobileFlowCard>

              {(items?.length ?? 0) > 0 && (
                <SearchInput
                  searchTerm={itemsSearch}
                  onSearchChange={setItemsSearch}
                  placeholder="Search added items..."
                />
              )}

              {itemsSearch.trim() && filteredItems.length === 0 ? (
                <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                  No added items match your search.
                </p>
              ) : null}

              {filteredItems.map((item, index) => (
                  <MobileFlowCard
                    key={getItemKey(item) || index}
                    title={item.description}
                    description={[item.length, item.unit_weight]
                      .filter(Boolean)
                      .join(" • ")}
                    action={
                      <button
                        type="button"
                        aria-label={`Remove ${item.description}`}
                        onClick={() => handleRemoveItem(item)}
                        className="flex h-9 w-9 items-center justify-center rounded-xl border-0 bg-red-50 p-0 text-red-600 active:bg-red-100"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    }
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-3 rounded-xl bg-gray-50 px-3 py-2 text-xs">
                        <span className="text-gray-500">Available</span>
                        <span className="flex items-center gap-2">
                          <Badge variant="onhold">{item.quantity}</Badge>
                          <span className="text-gray-500">
                            {kg(item.unit_weight, item?.quantity ?? 0)}
                          </span>
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <MobileFlowField
                          label={getQuantityColumnName(activitySubType)}
                          hint={kg(
                            item.unit_weight,
                            item?.rental_quantity ?? 0,
                          )}
                        >
                          <Input
                            type="text"
                            inputMode="numeric"
                            value={item.rental_quantity ?? ""}
                            aria-label="Rental quantity"
                            className={MOBILE_FIELD_CLASS}
                            onChange={(e) =>
                              handleRentalQuantityChange(item, e.target.value)
                            }
                          />
                        </MobileFlowField>
                        {(activitySubType === PURCHASE ||
                          activitySubType === RETURN) && (
                          <MobileFlowField
                            label="Damaged Qty"
                            hint={kg(
                              item.unit_weight,
                              item?.damaged_quantity ?? 0,
                            )}
                          >
                            <Input
                              type="text"
                              inputMode="numeric"
                              value={item.damaged_quantity ?? ""}
                              aria-label="Damaged quantity"
                              className={MOBILE_FIELD_CLASS}
                              onChange={(e) =>
                                handleDamagedQuantityChange(
                                  item,
                                  e.target.value,
                                )
                              }
                            />
                          </MobileFlowField>
                        )}
                      </div>
                      {activitySubType === RETURN && (
                        <div className="flex items-center justify-between gap-3 rounded-xl bg-gray-50 px-3 py-2 text-xs">
                          <span className="text-gray-500">Total</span>
                          <span className="flex items-center gap-2">
                            <Badge variant="onhold">
                              {getReturnTotalQty(item)}
                            </Badge>
                            <span className="text-gray-500">
                              {kg(item.unit_weight, getReturnTotalQty(item))}
                            </span>
                          </span>
                        </div>
                      )}
                      {activitySubType === RETURN && (
                        <MobileFlowField label="Reason">
                          <Input
                            type="text"
                            placeholder="Reason for return"
                            value={item.reason ?? ""}
                            className={MOBILE_FIELD_CLASS}
                            onChange={(e) =>
                              handleReasonChange(item, e.target.value)
                            }
                          />
                        </MobileFlowField>
                      )}
                    </div>
                  </MobileFlowCard>
                ))}

              {showInventoryItems && showReturnAddItems && (
                <div className="space-y-1.5">
                  <button
                    type="button"
                    disabled={
                      isPurchaseWithoutDestinationWarehouse ||
                      isMissingSourceSite
                    }
                    onClick={() => setSelectedItems(items ?? [])}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-primary-300 bg-primary-50 text-sm font-semibold text-primary-700 active:bg-primary-100 disabled:opacity-50"
                  >
                    <Icons.Plus className="h-4 w-4" />
                    Add items
                  </button>
                  {isPurchaseWithoutDestinationWarehouse && (
                    <p className="px-1 text-xs text-gray-500">
                      Choose a destination warehouse first to load that
                      warehouse’s inventory.
                    </p>
                  )}
                  {isMissingSourceSite && (
                    <p className="px-1 text-xs text-gray-500">
                      Choose a source site first to load items on site.
                    </p>
                  )}
                </div>
              )}

              {activitySubType !== LOST && (
                <MobileFlowCard title="Driver & vehicle" description="Optional">
                  <div className="space-y-4">
                    <MobileFlowField
                      label="Driver name"
                      htmlFor="driverName-mobile"
                    >
                      <Input
                        id="driverName-mobile"
                        value={driverName}
                        onChange={(e) => setDriverName(e.target.value)}
                        placeholder="Driver name"
                        autoComplete="name"
                        className={MOBILE_FIELD_CLASS}
                      />
                    </MobileFlowField>
                    <MobileFlowField
                      label="Driver phone number"
                      htmlFor="driverPhoneNumber-mobile"
                    >
                      <Input
                        id="driverPhoneNumber-mobile"
                        value={driverPhoneNumber}
                        onChange={(e) => setDriverPhoneNumber(e.target.value)}
                        placeholder="Driver phone"
                        inputMode="tel"
                        autoComplete="tel"
                        className={MOBILE_FIELD_CLASS}
                      />
                    </MobileFlowField>
                    <MobileFlowField
                      label="Vehicle number"
                      htmlFor="vehicleNumber-mobile"
                    >
                      <Input
                        id="vehicleNumber-mobile"
                        value={vehicleNumber}
                        onChange={(e) => setVehicleNumber(e.target.value)}
                        placeholder="Vehicle number"
                        autoCapitalize="characters"
                        className={MOBILE_FIELD_CLASS}
                      />
                    </MobileFlowField>
                  </div>
                </MobileFlowCard>
              )}

              <MobileFlowCard title="Notes" description="Optional">
                <Textarea
                  value={notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setNotes(e.target.value)
                  }
                  placeholder="Enter explanations here..."
                  className="h-32 resize-none rounded-xl"
                />
              </MobileFlowCard>
            </>
          )}
        </MobileFlowLayout>
      </>
    );
  }

  return (
    <>
      {flowOverlays}
      <div className="flex h-full min-w-0 flex-col overflow-hidden bg-white">
          <div className="shrink-0 border-b px-8 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 space-y-4">
                <Breadcrumb items={breadcrumbItems} />
                <h1 className="text-2xl font-bold font-jakarta">
                  Create Movement Activity
                </h1>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 text-sm"
                  disabled={loading}
                  onClick={handleSaveDraft}
                >
                  Save Draft
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 text-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                  disabled={loading}
                  onClick={handleClearDraft}
                >
                  Clear Draft
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 text-sm flex items-center gap-1.5"
                  onClick={() => {
                    setIsPreviewOpen(true);
                  }}
                >
                  <Icons.Preview className="h-4 w-4" />
                  Preview
                </Button>
                <Button
                  disabled={isCreateDisabled}
                  type="button"
                  className="h-9 text-sm bg-primary-600 hover:bg-primary-700 text-white"
                  onClick={handleCreateMovement}
                >
                  {loading ? "Creating..." : "Create Movement"}
                </Button>
                <div className="h-4 w-px bg-gray-200 mx-2" />
                <Button
                  type="button"
                  variant="ghost"
                  className="h-9 w-9 p-0 text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => {
                    navigate("/activities");
                  }}
                >
                  <Icons.X className="h-4 w-4 cursor-pointer" />
                </Button>
              </div>
            </div>
            <FormStepper
              orientation="horizontal"
              className="mt-4"
              steps={STEPS}
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={setCurrentStep}
            />
          </div>

          <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-8 py-6">
            <div
              className={cn(
                "mx-auto w-full space-y-6 text-sm text-gray-800",
                currentStep === STEPS[1].id ? "max-w-6xl" : "max-w-3xl",
              )}
            >
              {currentStep === STEPS[0].id && (
                <>
                  <Accordion
                    collapsible
                    type="single"
                    className="w-full"
                    value={openOutflowActivities}
                    onValueChange={() => {
                      setOpenOutflowActivities(undefined);
                    }}
                  >
                    <AccordionItem value={OUTFLOW}>
                      <AccordionTrigger>
                        <Label htmlFor="source" className="font-medium">
                          Outflow Activities
                        </Label>
                      </AccordionTrigger>
                      <AccordionContent>
                        <RadioGroupCards
                          options={OUTFLOW_ACTIVITIES}
                          value={activitySubType}
                          onChange={(value) => {
                            handleSelectActivity(OUTFLOW, value);
                            setOpenInflowActivities(undefined);
                          }}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                  <Accordion
                    collapsible
                    type="single"
                    className="w-full"
                    value={openInflowActivities}
                    onValueChange={() => {
                      setOpenInflowActivities(undefined);
                    }}
                  >
                    <AccordionItem value={INFLOW}>
                      <AccordionTrigger>
                        <Label htmlFor="source" className="font-medium">
                          Inflow Activities
                        </Label>
                      </AccordionTrigger>
                      <AccordionContent>
                        <RadioGroupCards
                          options={INFLOW_ACTIVITIES}
                          value={activitySubType}
                          onChange={(value) => {
                            handleSelectActivity(INFLOW, value);
                            setOpenOutflowActivities(undefined);
                          }}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                  {activityType && activitySubType && renderLocationAndDate()}
                </>
              )}
              {currentStep === STEPS[1].id && (
                <>
                  <section>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="job" className="font-medium">
                        Movement Type
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          handleEditActivityTypeLocationAndDate();
                        }}
                        className="h-9 px-3 text-sm text-primary-600 hover:bg-primary-50"
                      >
                        Edit
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500">
                      {activityType} - {activitySubType}
                      {activitySubType === RETURN && returnType
                        ? ` (${returnType === "Full" ? "Full return" : "Partial return"})`
                        : ""}
                    </p>
                  </section>
                  <section>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="job" className="font-medium">
                        Location Details
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          handleEditActivityTypeLocationAndDate();
                        }}
                        className="h-9 px-3 text-sm text-primary-600 hover:bg-primary-50"
                      >
                        Edit
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-700">
                        <span className="text-gray-500">Source:</span> {source}
                      </p>
                      {requiresDestination && (
                        <p className="text-sm text-gray-700">
                          <span className="text-gray-500">Destination:</span>{" "}
                          {destination}
                        </p>
                      )}
                    </div>
                  </section>
                  <section>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="job" className="font-medium">
                        Planned Date
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          handleEditActivityTypeLocationAndDate();
                        }}
                        className="h-9 px-3 text-sm text-primary-600 hover:bg-primary-50"
                      >
                        Edit
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500">
                      {plannedDate
                        ? plannedDate.toLocaleDateString()
                        : "No date selected"}
                    </p>
                  </section>
                  {(activitySubType === DELIVER ||
                    activitySubType === SITE_TRANSFER) && (
                    <section>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="daysHired" className="font-medium">
                          Number of days hired
                        </Label>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            handleEditActivityTypeLocationAndDate();
                          }}
                          className="h-9 px-3 text-sm text-primary-600 hover:bg-primary-50"
                        >
                          Edit
                        </Button>
                      </div>
                      <p className="text-sm text-gray-500">
                        {daysHired === "" || typeof daysHired !== "number"
                          ? "0"
                          : daysHired}{" "}
                        days
                      </p>
                    </section>
                  )}
                  {activitySubType === RETURN && (
                    <div className="space-y-2">
                      <Label className="font-medium">Return Type</Label>
                      <RadioGroupCards
                        options={RETURN_TYPES}
                        value={returnType}
                        onChange={handleReturnTypeChange}
                        className="grid-cols-2"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="destination" className="font-medium">
                        Items
                      </Label>
                      <div className="flex min-w-0 items-center gap-2">
                      {(items?.length ?? 0) > 0 && (
                        <SearchInput
                          searchTerm={itemsSearch}
                          onSearchChange={setItemsSearch}
                          placeholder="Search added items..."
                          className="max-w-[240px]"
                        />
                      )}
                      {showInventoryItems && showReturnAddItems && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-primary-600 hover:bg-primary-50"
                          title={
                            isPurchaseWithoutDestinationWarehouse
                              ? "Choose a destination warehouse first to load that warehouse’s inventory."
                              : isMissingSourceSite
                                ? "Choose a source site first to load items on site."
                                : undefined
                          }
                          disabled={
                            isPurchaseWithoutDestinationWarehouse ||
                            isMissingSourceSite
                          }
                          onClick={() => {
                            setSelectedItems(items ?? []);
                          }}
                        >
                          <Icons.Plus className="h-4 w-4 mr-1" />
                          Add Items
                        </Button>
                      )}
                      </div>
                    </div>
                    <div className="min-w-0 space-y-3 overflow-x-auto">
                      {activitySubType === RETURN && !returnType ? (
                        <p className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                          Choose Partial or Full return to continue.
                        </p>
                      ) : activitySubType === RETURN &&
                        returnType === "Full" &&
                        siteInventoryQuery.isFetching &&
                        (!items || items.length === 0) ? (
                        <p className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                          Loading items on site…
                        </p>
                      ) : (
                      <Table>
                        <TableHeader>
                          <TableHeaderRow>
                            <TableColumnHeader>S/No</TableColumnHeader>
                            <TableColumnHeader>
                              Description/Length/Weight(KG)
                            </TableColumnHeader>
                            <TableColumnHeader>
                              Available Qty
                            </TableColumnHeader>
                            <TableColumnHeader>
                              {getQuantityColumnName(activitySubType)}
                            </TableColumnHeader>
                            {(activitySubType === PURCHASE ||
                              activitySubType === RETURN) && (
                              <TableColumnHeader>
                                Damaged Qty
                              </TableColumnHeader>
                            )}
                            {activitySubType === RETURN && (
                              <TableColumnHeader>Total</TableColumnHeader>
                            )}
                            {activitySubType === RETURN && (
                              <TableColumnHeader>Reason</TableColumnHeader>
                            )}
                            <TableColumnHeader>Delete</TableColumnHeader>
                          </TableHeaderRow>
                        </TableHeader>
                        <TableBody>
                          {itemsSearch.trim() && filteredItems.length === 0 ? (
                            <TableRow className="border">
                              <TableCell
                                colSpan={
                                  activitySubType === RETURN
                                    ? 8
                                    : activitySubType === PURCHASE
                                      ? 6
                                      : 5
                                }
                                className="py-6 text-center text-sm text-gray-500"
                              >
                                No added items match your search.
                              </TableCell>
                            </TableRow>
                          ) : null}
                          {filteredItems.map((item, index) => {
                            const rowNumber =
                              (items ?? []).findIndex(
                                (listed) =>
                                  getItemKey(listed) === getItemKey(item),
                              ) + 1;
                            return (
                              <TableRow
                                key={getItemKey(item) || index}
                                className="border"
                              >
                                <TableCell>
                                  {rowNumber > 0 ? rowNumber : index + 1}
                                </TableCell>
                                <TableCell>
                                  <p>{item.description}</p>
                                  <p className="text-xs text-gray-500">
                                    {[item.length, item.unit_weight]
                                      .filter(Boolean)
                                      .join(" • ")}
                                  </p>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="onhold">
                                    {item.quantity}
                                  </Badge>
                                  <p className="text-xs text-gray-500">
                                    {Math.floor(
                                      item.unit_weight *
                                        (item?.quantity ?? 0),
                                    )}{" "}
                                    KG
                                  </p>
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="text"
                                    value={item.rental_quantity ?? ""}
                                    aria-label="Rental quantity"
                                    onChange={(e) =>
                                      handleRentalQuantityChange(
                                        item,
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <p className="text-xs text-gray-500">
                                    {Math.floor(
                                      item.unit_weight *
                                        (item?.rental_quantity ?? 0),
                                    )}{" "}
                                    KG
                                  </p>
                                </TableCell>
                                {(activitySubType === PURCHASE ||
                                  activitySubType === RETURN) && (
                                  <TableCell>
                                    <Input
                                      type="text"
                                      value={item.damaged_quantity ?? ""}
                                      aria-label="Damaged quantity"
                                      onChange={(e) =>
                                        handleDamagedQuantityChange(
                                          item,
                                          e.target.value,
                                        )
                                      }
                                    />
                                    <p className="text-xs text-gray-500">
                                      {Math.floor(
                                        item.unit_weight *
                                          (item?.damaged_quantity ?? 0),
                                      )}{" "}
                                      KG
                                    </p>
                                  </TableCell>
                                )}
                                {activitySubType === RETURN && (
                                  <TableCell>
                                    <Badge variant="onhold">
                                      {getReturnTotalQty(item)}
                                    </Badge>
                                    <p className="text-xs text-gray-500">
                                      {Math.floor(
                                        item.unit_weight *
                                          getReturnTotalQty(item),
                                      )}{" "}
                                      KG
                                    </p>
                                  </TableCell>
                                )}
                                {activitySubType === RETURN && (
                                  <TableCell>
                                    <Input
                                      type="text"
                                      placeholder="Reason for return"
                                      value={item.reason ?? ""}
                                      className="min-w-[120px] text-sm"
                                      onChange={(e) =>
                                        handleReasonChange(
                                          item,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </TableCell>
                                )}
                                <TableCell>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-gray-500 hover:text-red-600 hover:bg-red-50 h-8 px-2 shrink-0"
                                    onClick={() => handleRemoveItem(item)}
                                  >
                                    <Icons.Delete className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow className="border">
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell>
                              <Badge variant="onhold">
                                {sumItems((item) => item?.quantity ?? 0)}
                              </Badge>
                              <p className="text-xs text-gray-500">
                                {sumItems((item) =>
                                  Math.floor(
                                    item.unit_weight * (item?.quantity ?? 0),
                                  ),
                                )}{" "}
                                KG
                              </p>
                            </TableCell>
                            <TableCell>
                              <Badge variant="open">
                                {sumItems(
                                  (item) => item?.rental_quantity ?? 0,
                                )}
                              </Badge>
                              <p className="text-xs text-gray-500">
                                {sumItems((item) =>
                                  Math.floor(
                                    item.unit_weight *
                                      (item?.rental_quantity ?? 0),
                                  ),
                                )}{" "}
                                KG
                              </p>
                            </TableCell>
                            {(activitySubType === PURCHASE ||
                              activitySubType === RETURN) && (
                              <TableCell>
                                <Badge variant="destructive">
                                  {sumItems(
                                    (item) => item?.damaged_quantity ?? 0,
                                  )}
                                </Badge>
                                <p className="text-xs text-gray-500">
                                  {sumItems((item) =>
                                    Math.floor(
                                      item.unit_weight *
                                        (item?.damaged_quantity ?? 0),
                                    ),
                                  )}{" "}
                                  KG
                                </p>
                              </TableCell>
                            )}
                            {activitySubType === RETURN && (
                              <TableCell>
                                <Badge variant="onhold">
                                  {sumItems(getReturnTotalQty)}
                                </Badge>
                                <p className="text-xs text-gray-500">
                                  {sumItems((item) =>
                                    Math.floor(
                                      item.unit_weight *
                                        getReturnTotalQty(item),
                                    ),
                                  )}{" "}
                                  KG
                                </p>
                              </TableCell>
                            )}
                            {activitySubType === RETURN && (
                              <TableCell></TableCell>
                            )}
                            <TableCell></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      )}
                    </div>
                  </div>
                  {activitySubType !== LOST && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="driverName" className="font-medium">
                          Driver name (optional)
                        </Label>
                        <Input
                          id="driverName"
                          value={driverName}
                          onChange={(e) => setDriverName(e.target.value)}
                          placeholder="Driver name"
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="driverPhoneNumber"
                          className="font-medium"
                        >
                          Driver phone number (optional)
                        </Label>
                        <Input
                          id="driverPhoneNumber"
                          value={driverPhoneNumber}
                          onChange={(e) => setDriverPhoneNumber(e.target.value)}
                          placeholder="Driver phone"
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vehicleNumber" className="font-medium">
                          Vehicle number (optional)
                        </Label>
                        <Input
                          id="vehicleNumber"
                          value={vehicleNumber}
                          onChange={(e) => setVehicleNumber(e.target.value)}
                          placeholder="Vehicle number"
                          className="h-9"
                        />
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="destination" className="font-medium">
                      Notes(Optional)
                    </Label>
                    <Textarea
                      value={notes}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setNotes(e.target.value)
                      }
                      placeholder="Enter explanations here..."
                      className="h-48 resize-none"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-between border-t bg-white px-8 py-4">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={isFirstStep}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            {!isLastStep && (
              <Button
                disabled={
                  !activityType || !activitySubType || !source || !destination
                }
                onClick={handleNext}
                className="bg-gray-900 hover:bg-gray-800"
              >
                Next Step
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
      </div>
    </>
  );
};

export default AddActivity;
